import { useEffect, useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useForm, SubmitHandler } from "react-hook-form";
import { useRouteContext } from "@tanstack/react-router";

import { FAMIAddEditForm } from "@/components/records/child/FAMIAddEditForm";
import Popup from "@/components/shared/Popup";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { focusFirstInvalidField } from "@/utils/forms";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import { useLeaData } from "@/queries.ts";
import { FAMI_TEMPLATE, mutationFunctions, type FAMI } from "@/models/fami";
import {
  FiArrowLeft,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiTrash2,
} from "react-icons/fi";

export type FAMIEditDialogProps = {
  selectedFamily: FAMI | undefined;
  familyDefaults?: Partial<FAMI>;
  onClose: () => void;
  viewOnly?: boolean;
  onDeleted?: (record: FAMI) => void;
  footerMode?: "default" | "back";
};

export default function FAMIEditDialog({
  selectedFamily,
  familyDefaults,
  onClose,
  viewOnly = false,
  onDeleted,
  footerMode = "default",
}: FAMIEditDialogProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const leaData = useLeaData(leaContext.currentUserOrg.esiIdPath).data;

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState<{
    data: FAMI;
    dateChanged: boolean;
  } | null>(null);

  const initialValues: FAMI = selectedFamily
    ? {
        ...selectedFamily,
        EffectiveStartDate: selectedFamily.EffectiveStartDate
          ? new Date(selectedFamily.EffectiveStartDate)
              .toISOString()
              .split("T")[0]
          : "",
      }
    : ({ ...FAMI_TEMPLATE, ...(familyDefaults ?? {}) } as FAMI);

  const addEditForm = useForm<FAMI>({ defaultValues: initialValues });

  useEffect(() => {
    if (selectedFamily) {
      const formatted: FAMI = {
        ...selectedFamily,
        EffectiveStartDate: selectedFamily.EffectiveStartDate
          ? new Date(selectedFamily.EffectiveStartDate)
              .toISOString()
              .split("T")[0]
          : "",
      };
      addEditForm.reset(formatted);
    } else {
      addEditForm.reset({ ...FAMI_TEMPLATE, ...(familyDefaults ?? {}) });
    }
    addEditForm.clearErrors();
  }, [selectedFamily, familyDefaults, addEditForm]);

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form: addEditForm,
      recordType: "FAMI",
      toastMessage: "Family record submitted.\nWaiting for validation...",
      onSuccess: onClose,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form: addEditForm,
    recordType: "FAMI",
    toastMessage: "Family record submitted for deletion",
    successMessage: "Family record deleted successfully",
    onSuccess: () => {
      if (selectedFamily) onDeleted?.(selectedFamily);
      onClose();
    },
  });

  const mutateFns = mutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = mutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<FAMI> = (x) => {
    if (!selectedFamily) {
      handleSubmitStart();
      mutateFns.handleSave(x);
      return;
    }
    const dateChanged =
      (selectedFamily.EffectiveStartDate ?? "") !==
      (x.EffectiveStartDate ?? "");
    setSubmitConfirm({ data: x, dateChanged });
  };

  const handleConfirmSubmit = () => {
    if (!submitConfirm) return;
    const data = submitConfirm.data;
    setSubmitConfirm(null);
    handleSubmitStart();
    mutateFns.handleSave(data);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (selectedFamily) {
      handleDeleteStart();
      deleteMutateFns.handleSave(selectedFamily, true);
    }
  };

  const formId = "frmFAMI";
  const hasRole = leaContext.hasRole("FAMIEdit");
  const canEdit = hasRole && !viewOnly;
  const isBack = footerMode === "back";

  const deleteButton =
    hasRole && selectedFamily ? (
      <LoaderButton
        variant="danger"
        type="button"
        onClick={() => setShowDeleteConfirm(true)}
        loading={isDeleteSubmitting}
        loadingText="Deleting..."
        className="d-flex align-items-center"
      >
        <FiTrash2 className="me-1" />
        Delete
      </LoaderButton>
    ) : undefined;

  return (
    <>
      <Popup
        show={true}
        enforceFocus={!showDeleteConfirm && !submitConfirm}
        header={
          selectedFamily == null ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit ? "Add Family Record" : "View Family Record"}
              </span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit ? "Edit Family Record" : "View Family Record"}
              </span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={canEdit ? formId : undefined}
        submitButtonText={
          selectedFamily ? (
            <span className="d-flex align-items-center">
              <FiEdit className="me-1" />
              Update
            </span>
          ) : (
            <span className="d-flex align-items-center">
              <FiSave className="me-1" />
              Save
            </span>
          )
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText={isBack ? undefined : "Cancel"}
        size="lg"
        onClose={onClose}
        onSecondButtonClick={isBack ? undefined : onClose}
        backButtonText={
          isBack ? (
            <div className="d-flex align-items-center gap-2">
              <FiArrowLeft size={16} />
              <span>Back</span>
            </div>
          ) : undefined
        }
        onBackButtonClick={isBack ? onClose : undefined}
        canEdit={canEdit}
        additionalFooterButton={isBack ? deleteButton : undefined}
        leftFooterButton={isBack ? undefined : deleteButton}
      >
        <Form
          id={formId}
          onSubmit={addEditForm.handleSubmit(onSubmit, () =>
            focusFirstInvalidField(`#${formId}`)
          )}
        >
          <FAMIAddEditForm
            selectedFamily={selectedFamily}
            form={addEditForm}
            familyDefaults={familyDefaults}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          recordKindLabel="Family record"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {submitConfirm && (
        <ConfirmModal
          title={
            submitConfirm.dateChanged
              ? "Confirm Effective Date Change"
              : "Confirm Record Update"
          }
          message={
            submitConfirm.dateChanged
              ? "The changes requested have a different effective date. They will insert a new record with these updates and automatically expire the old record."
              : "The changes requested will overwrite the existing record. If these changes became effective at a later date than when the record was originally input, please input the associated effective date."
          }
          confirmText="Submit"
          onCancel={() => setSubmitConfirm(null)}
          onConfirm={handleConfirmSubmit}
        />
      )}
    </>
  );
}
